import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ListVideo, Plus, Trash2, Edit3, BarChart3 } from "lucide-react";
import { Field, inp } from "@/components/admin";

export function WebinarSeriesDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [series, setSeries] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/webinar-series");
      setSeries(data);
    } catch (e) { toast.error("Failed to load series"); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const del = async (id) => {
    if (!window.confirm("Delete this series? Webinars assigned to it will become standalone (not deleted).")) return;
    try { await api.delete(`/admin/webinar-series/${id}`); toast.success("Deleted"); load(); onDone?.(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="btn-ghost text-sm" data-testid="open-webinar-series">
          <ListVideo className="w-4 h-4" /> Webinar Series
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ListVideo className="w-5 h-5 text-fuchsia-300" /> Webinar Series Management</DialogTitle>
          <div className="text-xs text-[var(--text-mute)]">Group multiple webinars into a series (e.g. "DM Masterclass — 4-part"). Public series page at <code className="text-fuchsia-300">/webinar-series</code>.</div>
        </DialogHeader>

        <div className="flex justify-end mb-3">
          <button onClick={() => { setEditing(null); setShowCreate(true); }} className="btn-primary text-sm" data-testid="create-series">
            <Plus className="w-4 h-4" /> New series
          </button>
        </div>

        <div className="space-y-2">
          {series.length === 0 && <div className="text-sm text-white/40 text-center py-8">No series yet. Create one to group related webinars.</div>}
          {series.map((s) => (
            <div key={s.id} className="rounded-xl border border-white/10 p-3 flex items-center gap-3" data-testid={`series-row-${s.id}`}>
              {s.cover_image && <img src={s.cover_image} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{s.title}</div>
                <div className="text-xs text-white/50 line-clamp-1">{s.description || "—"}</div>
                <div className="text-[10px] text-white/40 mt-0.5">{s.webinar_count} webinars • {s.active ? "🟢 Active" : "⚪ Inactive"}</div>
              </div>
              <button onClick={() => { setEditing(s); setShowCreate(true); }} className="p-1.5 rounded hover:bg-white/5"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => del(s.id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <SeriesFormDialog open={showCreate} editing={editing} onClose={() => { setShowCreate(false); setEditing(null); }} onSaved={() => { load(); onDone?.(); }} />
      </DialogContent>
    </Dialog>
  );
}

function SeriesFormDialog({ open, editing, onClose, onSaved }) {
  const empty = { title: "", description: "", cover_image: "", active: true };
  const [f, setF] = useState(empty);
  useEffect(() => { setF(editing ? { ...empty, ...editing } : empty); /* eslint-disable-next-line */ }, [editing, open]);

  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/admin/webinar-series/${editing.id}`, f);
      else await api.post("/admin/webinar-series", f);
      toast.success("Saved");
      onSaved(); onClose();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-lg">
        <DialogHeader><DialogTitle>{editing ? "Edit series" : "New webinar series"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Title"><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={inp} placeholder="e.g. Digital Marketing Masterclass" /></Field>
          <Field label="Description"><textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inp} /></Field>
          <Field label="Cover image URL"><input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} className={inp} placeholder="https://…" /></Field>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
            Active (visible publicly)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button className="btn-primary flex-1 justify-center" data-testid="submit-series">{editing ? "Save" : "Create"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Webinar Analytics Quick View
export function WebinarAnalyticsButton({ webinar }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !webinar?.id) return;
    api.get(`/admin/webinars/${webinar.id}/analytics`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [open, webinar?.id]);
  if (!webinar) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} className="btn-ghost !py-1.5 text-xs" title="Analytics" data-testid={`webinar-analytics-${webinar.id}`}>
        <BarChart3 className="w-3.5 h-3.5" /> Stats
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-elev-2)] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><BarChart3 className="w-5 h-5 text-fuchsia-300" /> Webinar Analytics</DialogTitle>
            <div className="text-xs text-white/50">{webinar.title}</div>
          </DialogHeader>
          {!data ? <div className="text-white/50 text-sm py-6 text-center">Loading…</div> : data.error ? <div className="text-red-300">Failed to load</div> : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Registrations", val: data.registrations, color: "fuchsia" },
                  { label: "Attended", val: data.attended, color: "emerald" },
                  { label: "Attendance %", val: data.attendance_rate + "%", color: "amber" },
                  { label: "Capacity fill", val: data.fill_rate + "%", color: "purple" },
                ].map((s) => (
                  <div key={s.label} className={`rounded-lg bg-${s.color}-500/10 border border-${s.color}-400/20 p-2.5`}>
                    <div className="text-[10px] uppercase text-white/50">{s.label}</div>
                    <div className="text-2xl font-bold mt-0.5">{s.val}</div>
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-3 text-xs">
                {[
                  { k: "Sources", obj: data.sources },
                  { k: "Top Cities", obj: data.top_cities },
                  { k: "Professions", obj: data.top_professions },
                ].map((g) => (
                  <div key={g.k} className="rounded-lg border border-white/10 p-2">
                    <div className="text-[10px] uppercase text-fuchsia-300 font-bold mb-1">{g.k}</div>
                    {Object.keys(g.obj || {}).length === 0 ? <div className="text-white/40 text-[11px]">No data</div> :
                      Object.entries(g.obj).slice(0, 8).map(([k, v]) => (
                        <div key={k} className="flex justify-between py-0.5">
                          <span className="truncate text-white/70">{k}</span><span className="text-fuchsia-300 font-semibold">{v}</span>
                        </div>
                      ))
                    }
                  </div>
                ))}
              </div>
              {data.webinar.replay_url && (
                <div className="text-xs rounded-lg bg-emerald-500/10 border border-emerald-400/20 p-2.5">
                  🎥 Replay available: <a href={data.webinar.replay_url} target="_blank" rel="noreferrer" className="text-emerald-300 underline">{data.webinar.replay_url}</a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default WebinarSeriesDialog;
